import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Drawer, Portal, CloseButton } from '@chakra-ui/react';
import PublicContent from '@/components/content-list/public-content';
import { PillGroup } from '@/components/ui/pill-group';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { Content, ContentType } from '@/lib/types';

interface Props {
  isOpen: boolean;
  onClose(): void;
  workspaceId: number;
  contentType: ContentType | undefined;
  contentRefs: string[];
  showPartner?: boolean;
  onSuccess(refs: string[], item?: Content): void;
}

interface Partner {
  name: string;
  content_type_id: number;
  workspace_id: number;
  content_counts: number;
  reference_id: string;
}

export default function ContentBlock({
  isOpen,
  onClose,
  workspaceId,
  contentType,
  contentRefs,
  showPartner = false,
  onSuccess,
}: Props) {
  const partnersLoaded = useRef(false);
  const [partnerId, setPartnerId] = useState<number>(workspaceId);
  const [referenceId, setReferenceId] = useState<string>();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [refs, setRefs] = useState<string[]>(contentRefs);

  const handleAdd = (content: Content) => {
    const updatedRefs = new Set([...refs, content.reference_id]);
    const updatedRefsArray = Array.from(updatedRefs);
    setRefs(updatedRefsArray);
    const item = {
      ...content,
      content_type_name: contentType?.name || '',
    };
    onSuccess(updatedRefsArray, item);
  };

  const handleRemove = (refId: string) => {
    const updatedRefs = new Set(refs);
    updatedRefs.delete(refId);
    const updatedRefsArray = Array.from(updatedRefs);
    setRefs(updatedRefsArray);
    onSuccess(updatedRefsArray);
  };

  const getPartners = useCallback(async () => {
    if (!workspaceId || !contentType || !contentType.id) {
      return;
    }
    setPartners([
      {
        name: 'My workspace',
        workspace_id: workspaceId,
        content_type_id: contentType.id,
        content_counts: 1,
        reference_id: contentType?.workspace_content_type_reference_id || contentType?.reference_id,
      },
    ]);

    try {
      const response = await api.get(`workspace/public-selected-workspaces/${workspaceId}`);
      if (response.data.data) {
        const partners = response.data.data
          .map((data: any) => {
            const matchingContentType = data.content_types.find(
              (type: any) => type.content_type_id === contentType.id,
            );

            return {
              name: data.workspace_name,
              workspace_id: data.workspace_id,
              content_type_id: contentType.id,
              content_counts: matchingContentType ? matchingContentType.content_counts : 0,
              reference_id:
                matchingContentType?.workspace_content_type_reference_id ||
                matchingContentType?.content_type_reference_id,
            };
          })
          .filter((partner: Partner) => partner.content_counts > 0);

        setPartners((prev) => [...prev, ...partners]);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch partners',
      });
    }
  }, [workspaceId, contentType]);

  useEffect(() => {
    if (isOpen) {
      setRefs(contentRefs);

      if (!partnersLoaded.current) {
        setPartnerId(workspaceId);

        const refId = contentType?.workspace_content_type_reference_id || contentType?.reference_id;

        setReferenceId(refId);
        if (showPartner) {
          getPartners();
        }
        partnersLoaded.current = true;
      }
    } else {
      partnersLoaded.current = false;
    }
  }, [isOpen, contentRefs, workspaceId, showPartner, contentType, getPartners]);

  const switchPartner = (partner: Partner) => {
    setPartnerId(partner.workspace_id);
    setReferenceId(partner.reference_id);
  };

  return (
    <Drawer.Root
      id={`content-block-drawer-${contentType?.id}`}
      size='xl'
      placement='end'
      open={isOpen}
      onOpenChange={onClose}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton size='sm' />
            </Drawer.CloseTrigger>
            {contentType && (
              <Drawer.Header>
                <Drawer.Title>{contentType.name}</Drawer.Title>
              </Drawer.Header>
            )}
            <Drawer.Body>
              {showPartner && (
                <Box mb={4}>
                  <PillGroup
                    items={partners.map((partner) => ({
                      value: partner.workspace_id,
                      label: partner.name,
                    }))}
                    value={partnerId}
                    onChange={(wsId) => {
                      const partner = partners.find((p) => p.workspace_id === wsId);
                      if (partner) switchPartner(partner);
                    }}
                  />
                </Box>
              )}
              {referenceId && (
                <PublicContent
                  referenceId={referenceId}
                  hasActions={true}
                  contentRefs={refs}
                  onAddContent={handleAdd}
                  onRemoveContent={handleRemove}
                />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
